// src/components/ModerateBooks.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useUser } from "../contexts/UserContext";
import AppHeader from "./AppHeader";

const ModerateBooks = () => {
    const { user, isAdmin, loading } = useUser();
    const [books, setBooks] = useState([]);
    const [updatingId, setUpdatingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [loadingBooks, setLoadingBooks] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        if (!loading && (!user || !isAdmin)) {
            console.warn("User is not an admin. Redirecting.");
            navigate("/manage-book");
        }
    }, [user, isAdmin, loading, navigate]);

    useEffect(() => {
        const fetchBooks = async () => {
            try {
                const snapshot = await getDocs(collection(db, "books"));
                const data = await Promise.all(
                    snapshot.docs.map(async (bookDoc) => {
                        const book = bookDoc.data();
                        const authorDoc = await getDoc(doc(db, "users", bookDoc.id));
                        const email = authorDoc.exists() ? authorDoc.data().email : "Unknown";
                        return {
                            id: bookDoc.id,
                            title: book.bookTitle,
                            author: book.authorName,
                            published: book.published,
                            contentCount: book.contents?.length || 0,
                            email,
                        };
                    })
                );
                setBooks(data);
            } catch (err) {
                console.error("Error loading books:", err);
            } finally {
                setLoadingBooks(false);
            }
        };

        if (user && isAdmin) {
            fetchBooks();
        }
    }, [user, isAdmin]);

    const togglePublished = async (bookId, currentState) => {
        setUpdatingId(bookId);
        try {
            await updateDoc(doc(db, "books", bookId), {
                published: !currentState,
            });
            setBooks((prev) =>
                prev.map((book) =>
                    book.id === bookId ? { ...book, published: !currentState } : book
                )
            );
        } catch (err) {
            alert("Failed to update published status");
        } finally {
            setUpdatingId(null);
        }
    };

    const filteredBooks = books.filter((book) => {
        const term = searchTerm.toLowerCase();
        return (
            book.title?.toLowerCase().includes(term) ||
            book.author?.toLowerCase().includes(term) ||
            book.email?.toLowerCase().includes(term)
        );
    });

    if (loading) {
        return <div className="container mt-5">Checking access...</div>;
    }

    return (
        <div className="container mt-5">
            <AppHeader />
            <h2 className="mb-4 text-center">Moderate Books</h2>

            <div className="mb-4">
                <input
                    type="text"
                    className="form-control"
                    placeholder="Search by title, author, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {loadingBooks ? (
                <p>Loading books...</p>
            ) : filteredBooks.length === 0 ? (
                <p>No books found.</p>
            ) : (
                <table className="table table-bordered">
                    <thead className="table-light">
                        <tr>
                            <th>Title</th>
                            <th>Author</th>
                            <th>Email</th>
                            <th>Pages</th>
                            <th>Published</th>
                            <th>Toggle</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredBooks.map((book) => (
                            <tr key={book.id}>
                                <td>{book.title || "Untitled"}</td>
                                <td>{book.author || "Unknown"}</td>
                                <td>{book.email}</td>
                                <td>{book.contentCount}</td>
                                <td>
                                    <span className={`badge ${book.published ? "bg-success" : "bg-secondary"}`}>
                                        {book.published ? "Yes" : "No"}
                                    </span>
                                </td>
                                <td>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => togglePublished(book.id, book.published)}
                                        disabled={updatingId === book.id}
                                    >
                                        {book.published ? "Unpublish" : "Publish"}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default ModerateBooks;
